import React, { useState, useRef, useEffect } from "react";
import { FaMicrophone } from "react-icons/fa";
import * as speechSDK from "microsoft-cognitiveservices-speech-sdk";
import { Globalstate } from "context/context";
import { API_BASE_URL } from "config";
import { franc } from "franc-min";

function MultilingualSpeechRecognition({
  speechRegion = "centralindia",
  handleSubmit
}) {
  const [isListening, setIsListening] = useState(false);
  const [tokenData, setTokenData] = useState(null);

  const TOKEN_REFRESH_INTERVAL = 5 * 60 * 1000;
  const tokenIntervalRef = useRef(null);
  const recognizerRef = useRef(null);
  const audioConfigRef = useRef(null);
  const speechConfigRef = useRef(null);

  /* ---------------- LANGUAGE DETECTOR ---------------- */

  function detectRealLanguage(text) {
    if (!text || text.length < 3) return "en";

    const lang = franc(text);

    const map = {
      eng: "en",
      hin: "hi",
      tel: "te",
      tam: "ta",
      kan: "kn",
      mar: "mr",
      guj: "gu",
      pan: "pa",
      ben: "bn"
    };

    return map[lang] || "en";
  }

  /* ---------------- TOKEN ---------------- */

  async function fetchSpeechToken() {
    try {
      const authToken = localStorage.getItem("authToken");

      const response = await fetch(API_BASE_URL + "/get_speech_token", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`
        }
      });

      if (!response.ok) throw new Error("Failed to fetch speech token");

      const data = await response.json();

      return { token: data.token, region: speechRegion, fetchedAt: Date.now() };
    } catch (error) {
      console.error("Speech token error:", error);
      throw error;
    }
  }

  /* ---------------- SPEECH CONFIG ---------------- */

  const initializeSpeechConfig = () => {
    if (!tokenData) return null;

    speechConfigRef.current = speechSDK.SpeechConfig.fromAuthorizationToken(
      tokenData.token,
      tokenData.region
    );

    // FORCE ENGLISH ACOUSTIC MODEL (handles all Indian accents)
    speechConfigRef.current.speechRecognitionLanguage = "en-IN";

    speechConfigRef.current.setProfanity(speechSDK.ProfanityOption.Raw);
    speechConfigRef.current.outputFormat = speechSDK.OutputFormat.Detailed;

    // Prevent early cutoff
    speechConfigRef.current.setProperty(
      speechSDK.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs,
      "5000"
    );

    return speechConfigRef.current;
  };

  /* ---------------- CREATE RECOGNIZER ---------------- */

  const createRecognizer = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const speechConfig = initializeSpeechConfig();
      if (!speechConfig) return null;

      if (!audioConfigRef.current)
        audioConfigRef.current =
          speechSDK.AudioConfig.fromDefaultMicrophoneInput();

      // IMPORTANT: No AutoDetectSourceLanguage anymore
      const recognizer = new speechSDK.SpeechRecognizer(
        speechConfig,
        audioConfigRef.current
      );

      return recognizer;
    } catch (error) {
      console.error("Recognizer error:", error);
      return null;
    }
  };

  /* ---------------- START ---------------- */

  const startRecognition = async () => {
    if (isListening) return;

    try {
      recognizerRef.current = await createRecognizer();
      if (!recognizerRef.current) return;

      recognizerRef.current.recognized = (s, e) => {
        if (e.result.reason === speechSDK.ResultReason.RecognizedSpeech) {

          const text = e.result.text.trim();

          // Detect real language from words
          const detectedLang = detectRealLanguage(text);

          console.log("TEXT:", text);
          console.log("LANG:", detectedLang);

          handleSubmit(text, detectedLang);
          stopRecognition();
        }
      };

      await recognizerRef.current.startContinuousRecognitionAsync();
      setIsListening(true);
    } catch (error) {
      console.error("Start error:", error);
      stopRecognition();
    }
  };

  /* ---------------- STOP ---------------- */

  const stopRecognition = async () => {
    if (!recognizerRef.current) return;

    try {
      await recognizerRef.current.stopContinuousRecognitionAsync();
      recognizerRef.current.close();
      recognizerRef.current = null;
      setIsListening(false);
    } catch (error) {
      console.error("Stop error:", error);
    }
  };

  const toggleRecognition = async () => {
    if (isListening) await stopRecognition();
    else await startRecognition();
  };

  /* ---------------- TOKEN REFRESH ---------------- */

  const startTokenRefresh = () => {
    if (tokenIntervalRef.current) clearInterval(tokenIntervalRef.current);

    tokenIntervalRef.current = setInterval(async () => {
      try {
        const newTokenData = await fetchSpeechToken();
        setTokenData(newTokenData);
        speechConfigRef.current = null;
        initializeSpeechConfig();
      } catch (error) {
        console.error("Token refresh error:", error);
      }
    }, TOKEN_REFRESH_INTERVAL);
  };

  useEffect(() => {
    fetchSpeechToken()
      .then((newTokenData) => {
        setTokenData(newTokenData);
        startTokenRefresh();
      })
      .catch(console.error);

    return () => {
      if (tokenIntervalRef.current) clearInterval(tokenIntervalRef.current);
      stopRecognition();
      audioConfigRef.current = null;
      speechConfigRef.current = null;
    };
  }, []);

  /* ---------------- UI ---------------- */

  return (
    <div className="flex flex-col items-center space-y-4">
      <button
        onClick={toggleRecognition}
        className={`p-4 rounded-full transition-all duration-200 ${isListening
          ? "bg-red-500 hover:bg-red-600"
          : "bg-white hover:bg-gray-200"
          }`}
        disabled={!tokenData}
      >
        <FaMicrophone
          className={`w-[23px] h-[23px] ${isListening ? "animate-pulse text-white" : ""
            }`}
        />
      </button>
    </div>
  );
}

export default MultilingualSpeechRecognition;
